import { action, makeObservable, observable, runInAction } from 'mobx';
import { NotificationManager } from '../components/notification-manager';
import { CmsUserHistory, HttpMethod, PaginatedList } from '../types';
import { AppTarget } from '../types/app-selection';
import { RootStore } from './root';

export type SectionPlacementTranslation = {
    language?: 'en' | 'fr';
    lang?: 'en' | 'fr';
    title?: string | null;
    subTitle?: string | null;
    subtitle?: string | null;
    image?: string | null;
    thumbnail?: string | null;
};

export type SectionPlacementFeaturedItem = {
    id: number;
    isActive: boolean;
    canBeDeleted: boolean;
    geolocated: boolean | null;
    type: string;
    position: number;
    gameType: string | null;
    gameId: number | null;
    promotionId: number | null;
    startDate: string;
    endDate: string;
    timezone: string;
    createdAt: string;
    updatedAt: string;
    items: {
        backgroundImage: string;
        imageTitle: string | null;
        textTitle: string | null;
        textColor: string | null;
        cta: string | null;
        language: 'en' | 'fr';
        externalUrl: string | null;
        title: string | null;
        subTitle: string | null;
        backgroundColor: string | null;
        titleColor: string | null;
        buttonColor: string | null;
    }[];
};

export type SectionPlacementPromotionItem = {
    id: number;
    type: 'coupon' | 'in_store' | string;
    isActive: boolean;
    startDate: string;
    endDate: string;
    timezone: string;
    items: {
        title: string | null;
        subTitle: string | null;
        description?: string | null;
        thumbnail?: string | null;
        background?: string | null;
        backgroundColor?: string | null;
        promotedBannerTextColor?: string | null;
        language: 'en' | 'fr';
    }[];
};

export type SectionPlacementEventItem = {
    id: number;
    startDate: string;
    endDate: string;
    timezone: string;
    isActive: boolean;
    translations: SectionPlacementTranslation[];
};

export type SectionPlacementGameItem = {
    id: number;
    type?: string | null;
    status?: string | null;
    isActive?: boolean | null;
    startDate?: string | null;
    endDate?: string | null;
    timezone?: string | null;
    title?: Record<'en' | 'fr', string> | null;
    subTitle?: Record<'en' | 'fr', string> | null;
    image?: Record<'en' | 'fr', string> | null;
    translations?: SectionPlacementTranslation[];
};

export type SectionPlacementItemContent =
    | SectionPlacementFeaturedItem
    | SectionPlacementPromotionItem
    | SectionPlacementEventItem
    | SectionPlacementGameItem;

export type SectionPlacementItem = {
    id: number;
    placementId: number;
    contentType: string;
    contentId: number;
    position: number;
    config: Record<string, unknown> | null;
    createdAt: string;
    updatedAt: string;
    content: SectionPlacementItemContent | null;
};

export type SectionPlacement = {
    id: number;
    key: string;
    label: string;
    position: number;
    isActive: boolean;
    groupKey: string | null;
    allowedTypes: string[] | null;
    config: Record<string, unknown> | null;
    createdAt: string;
    updatedAt: string;
    items: SectionPlacementItem[];
};

export class SectionPlacementsStore {
    @observable placements: SectionPlacement[] = [];
    @observable placementsHistory: PaginatedList<CmsUserHistory> | null = null;
    @observable isFetchingPlacements = false;
    @observable isFetchingPlacementsHistory = false;
    @observable sortingPlacementIds: number[] = [];
    @observable deletingPlacementItemIds: number[] = [];
    private fetchPlacementsPromise: Promise<void> | null = null;
    private placementsTarget: AppTarget | null = null;

    constructor(private rootStore: RootStore) {
        makeObservable(this);
    }

    @action
    async fetchPlacements() {
        const appTarget = this.rootStore.applicationSelectionStore.appTarget;
        if (!appTarget) {
            this.placements = [];
            this.placementsTarget = null;
            return;
        }

        if (this.fetchPlacementsPromise) {
            await this.fetchPlacementsPromise;
            if (this.placementsTarget === appTarget) {
                return;
            }
        }

        const fetchPromise = this.fetchPlacementsForTarget(appTarget);
        this.fetchPlacementsPromise = fetchPromise;
        await fetchPromise;
        if (this.fetchPlacementsPromise === fetchPromise) {
            this.fetchPlacementsPromise = null;
        }
    }

    private async fetchPlacementsForTarget(appTarget: AppTarget) {
        runInAction(() => {
            this.isFetchingPlacements = true;
        });

        const response = await this.rootStore.makeNetworkCall<
            SectionPlacement[]
        >({
            method: HttpMethod.GET,
            url: '/section-placements',
            params: { appTarget },
        });

        runInAction(() => {
            this.isFetchingPlacements = false;
            const currentTarget = this.rootStore.applicationSelectionStore
                .appTarget;
            if (currentTarget !== appTarget) {
                return;
            }
            if (!response.err && response.data) {
                this.placements = this.normalizePlacements(response.data);
                this.placementsTarget = appTarget;
            }
        });
    }

    @action
    async fetchPlacementsHistory(page = 1, perPage = 10, action?: string) {
        if (this.isFetchingPlacementsHistory) {
            return;
        }

        runInAction(() => {
            this.isFetchingPlacementsHistory = true;
        });

        const response = await this.rootStore.makeNetworkCall<
            PaginatedList<CmsUserHistory>
        >({
            method: HttpMethod.GET,
            url: '/section-placements/history',
            params: { page, perPage, ...(action ? { action } : {}) },
        });

        runInAction(() => {
            this.isFetchingPlacementsHistory = false;
            if (!response.err && response.data) {
                this.placementsHistory = {
                    page: response.data.page,
                    perPage: response.data.perPage,
                    total: response.data.total,
                    results: response.data.results.map((item) => ({
                        email: item.email ?? null,
                        action: item.action,
                        createdAt: item.createdAt,
                    })),
                };
            }
        });
    }

    @action
    setPlacementItems(placementId: number, items: SectionPlacementItem[]) {
        this.placements = this.placements.map((placement) =>
            placement.id === placementId ? { ...placement, items } : placement
        );
    }

    @action
    async sortPlacementItems(placementId: number, itemIds: number[]) {
        if (this.sortingPlacementIds.includes(placementId)) {
            return false;
        }

        runInAction(() => {
            this.sortingPlacementIds = [
                ...this.sortingPlacementIds,
                placementId,
            ];
        });

        const response = await this.rootStore.makeNetworkCall({
            method: HttpMethod.PATCH,
            url: `/section-placements/${placementId}/items/sort`,
            data: { itemIds },
        });

        runInAction(() => {
            this.sortingPlacementIds = this.sortingPlacementIds.filter(
                (id) => id !== placementId
            );
        });

        if (!response.err) {
            NotificationManager.showSuccess('List updated');
        }

        return !response.err;
    }

    @action
    async deletePlacementItem(placementId: number, itemId: number) {
        if (this.deletingPlacementItemIds.includes(itemId)) {
            return false;
        }

        runInAction(() => {
            this.deletingPlacementItemIds = [
                ...this.deletingPlacementItemIds,
                itemId,
            ];
        });

        const response = await this.rootStore.makeNetworkCall({
            method: HttpMethod.DELETE,
            url: `/section-placements/${placementId}/items/${itemId}`,
        });

        runInAction(() => {
            this.deletingPlacementItemIds = this.deletingPlacementItemIds.filter(
                (id) => id !== itemId
            );
            if (!response.err) {
                this.placements = this.placements.map((placement) => {
                    if (placement.id !== placementId) return placement;
                    return {
                        ...placement,
                        items: placement.items.filter(
                            (item) => item.id !== itemId
                        ),
                    };
                });
            }
        });

        if (!response.err) {
            NotificationManager.showSuccess('Item removed');
        }

        return !response.err;
    }

    isSortingPlacement(placementId: number) {
        return this.sortingPlacementIds.includes(placementId);
    }

    private normalizePlacements(
        placements: SectionPlacement[]
    ): SectionPlacement[] {
        return placements
            .map((placement) => ({
                ...placement,
                items: placement.items
                    .filter((item) => item.content)
                    .sort((a, b) => a.position - b.position),
            }))
            .sort((a, b) => a.position - b.position);
    }
}
